import { useCallback, useEffect, useRef, useState } from 'react';

// Voice-activity heuristic. A frame counts as "voiced" when its linear RMS
// exceeds VOICE_RMS_THRESHOLD (~-38 dBFS — above typical ambient room noise,
// below normal speech). At stop we require at least MIN_VOICED_RATIO of the
// total recording duration to be voiced; otherwise the audio is rejected
// client-side as empty speech, so nothing reaches GCS or Whisper.
const VOICE_RMS_THRESHOLD = 0.012;
const MIN_VOICED_RATIO = 0.20;

/**
 * Voice-journal recording hook. Owns MediaRecorder, AudioContext + Analyser
 * (for the level meter + post-stop voice-activity gate), elapsed timer, and
 * auto-stop at the configured cap.
 *
 * Returns:
 *   state          'idle' | 'requesting-permission' | 'permission-denied'
 *                  | 'recording' | 'ready-to-upload' | 'empty-speech' | 'error'
 *   start()        async — request mic, begin recording
 *   stop()         end recording cleanly; finalizes blob and runs VAD check
 *   cancel()       end + discard; returns hook to 'idle'
 *   reset()        clear all state without touching the recorder
 *   elapsedMs      current recording elapsed time (animation-frame cadence)
 *   level          { rms, peak, clipping } — all in [0,1]
 *   warningAt80    flips true once elapsed >= warnAtPct * maxDurationMs
 *   blob           Blob | null — populated when state === 'ready-to-upload'
 *   mimeType       string | null — MediaRecorder's selected mime type
 *   error          string | null
 */
export function useVoiceCapture({ maxDurationMs = 360000, warnAtPct = 0.8 } = {}) {
  const [state, setState] = useState('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState({ rms: 0, peak: 0, clipping: false });
  const [warningAt80, setWarningAt80] = useState(false);
  const [blob, setBlob] = useState(null);
  const [mimeType, setMimeType] = useState(null);
  const [error, setError] = useState(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const startTimeRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const voicedMsRef = useRef(0);
  const warnFiredRef = useRef(false);
  const autoStoppedRef = useRef(false);

  const cleanupAudioGraph = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch { /* ignore */ }
      });
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close().catch(() => {});
    }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, []);

  // Hard cleanup on unmount.
  useEffect(() => {
    return () => {
      try {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      } catch { /* ignore */ }
      cleanupAudioGraph();
    };
  }, [cleanupAudioGraph]);

  // iOS PWA / Safari background: if the tab is hidden while recording, the
  // audio session can be killed silently. Stop cleanly so the user keeps
  // whatever was captured rather than losing the entry.
  useEffect(() => {
    if (state !== 'recording') return;
    const onVisibility = () => {
      if (document.hidden && recorderRef.current && recorderRef.current.state !== 'inactive') {
        try { recorderRef.current.stop(); } catch { /* ignore */ }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [state]);

  const tick = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const now = performance.now();
    const dtMs = lastFrameTimeRef.current ? now - lastFrameTimeRef.current : 0;
    lastFrameTimeRef.current = now;

    const buf = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buf);

    let sumSq = 0;
    let peak = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = buf[i];
      sumSq += v * v;
      const a = v < 0 ? -v : v;
      if (a > peak) peak = a;
    }
    const rms = Math.sqrt(sumSq / buf.length);
    setLevel({ rms, peak, clipping: peak > 0.99 });

    if (rms > VOICE_RMS_THRESHOLD) {
      voicedMsRef.current += dtMs;
    }

    const elapsed = now - startTimeRef.current;
    setElapsedMs(elapsed);

    if (!warnFiredRef.current && elapsed >= maxDurationMs * warnAtPct) {
      warnFiredRef.current = true;
      setWarningAt80(true);
    }

    if (!autoStoppedRef.current && elapsed >= maxDurationMs) {
      autoStoppedRef.current = true;
      try { recorderRef.current?.stop(); } catch { /* ignore */ }
      return;
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [maxDurationMs, warnAtPct]);

  const start = useCallback(async () => {
    setError(null);
    setBlob(null);
    setMimeType(null);
    setElapsedMs(0);
    setLevel({ rms: 0, peak: 0, clipping: false });
    setWarningAt80(false);
    voicedMsRef.current = 0;
    warnFiredRef.current = false;
    autoStoppedRef.current = false;
    chunksRef.current = [];
    setState('requesting-permission');

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      const denied =
        err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      if (denied) {
        setState('permission-denied');
      } else {
        setError(err?.message || 'Could not access microphone');
        setState('error');
      }
      return;
    }
    streamRef.current = stream;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;

      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const recordedMime = recorder.mimeType || 'audio/webm';
        const finalBlob = new Blob(chunksRef.current, { type: recordedMime });
        const totalMs = startTimeRef.current
          ? performance.now() - startTimeRef.current
          : 0;
        const voicedMs = voicedMsRef.current;
        const voicedRatio = totalMs > 0 ? voicedMs / totalMs : 0;
        cleanupAudioGraph();
        // Reject if too much of the recording was silent / below speech level —
        // catches accidental taps, breathing-only, and ambient-noise-only audio
        // before it touches the network.
        if (voicedRatio < MIN_VOICED_RATIO) {
          setBlob(null);
          setMimeType(null);
          setState('empty-speech');
        } else {
          setBlob(finalBlob);
          setMimeType(recordedMime);
          setState('ready-to-upload');
        }
      };
      recorder.onerror = (e) => {
        cleanupAudioGraph();
        setError(e.error?.message || 'Recording error');
        setState('error');
      };

      recorder.start(250);
      startTimeRef.current = performance.now();
      lastFrameTimeRef.current = 0;
      setState('recording');
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      cleanupAudioGraph();
      setError(err?.message || 'Could not start recording');
      setState('error');
    }
  }, [tick, cleanupAudioGraph]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* onstop fires regardless */ }
    }
  }, []);

  const cancel = useCallback(() => {
    chunksRef.current = [];
    try {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.onstop = null;
        recorderRef.current.stop();
      }
    } catch { /* ignore */ }
    cleanupAudioGraph();
    setState('idle');
    setBlob(null);
    setMimeType(null);
    setElapsedMs(0);
    setLevel({ rms: 0, peak: 0, clipping: false });
    setWarningAt80(false);
    setError(null);
    voicedMsRef.current = 0;
    warnFiredRef.current = false;
    autoStoppedRef.current = false;
  }, [cleanupAudioGraph]);

  const reset = useCallback(() => {
    setState('idle');
    setBlob(null);
    setMimeType(null);
    setElapsedMs(0);
    setLevel({ rms: 0, peak: 0, clipping: false });
    setWarningAt80(false);
    setError(null);
    voicedMsRef.current = 0;
    warnFiredRef.current = false;
    autoStoppedRef.current = false;
  }, []);

  return {
    state,
    start,
    stop,
    cancel,
    reset,
    elapsedMs,
    level,
    warningAt80,
    blob,
    mimeType,
    error,
  };
}
